import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { clearTokens, getTokens, setTokens } from './db'

const baseURL = import.meta.env.VITE_API_BASE || ''

export const api = axios.create({
  baseURL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const tokens = await getTokens()
  if (tokens?.access_token) {
    config.headers.set('Authorization', `Bearer ${tokens.access_token}`)
  }
  return config
})

let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    const tokens = await getTokens()
    if (!tokens?.refresh_token) return null
    try {
      const resp = await axios.post(
        `${baseURL}/api/auth/refresh`,
        { refresh_token: tokens.refresh_token, device_id: tokens.device_id },
        { headers: { 'Content-Type': 'application/json' } },
      )
      const newAccess: string = resp.data.access_token
      await setTokens({ ...tokens, access_token: newAccess })
      return newAccess
    } catch {
      await clearTokens()
      return null
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !original.url?.includes('/api/auth/')
    ) {
      original._retried = true
      const newAccess = await refreshAccessToken()
      if (newAccess) {
        original.headers!.set('Authorization', `Bearer ${newAccess}`)
        return api(original)
      }
      // Refresh fail → volver a la pantalla de invitación
      window.location.assign('/onboard')
    }
    return Promise.reject(error)
  },
)
